import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, TrendingUp, TrendingDown, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface AlertHistoryWidgetProps {
  shopId: string;
  limit?: number;
}

export function AlertHistoryWidget({ shopId, limit = 10 }: AlertHistoryWidgetProps) {
  const { data: alerts, isLoading } = useQuery({
    queryKey: ['alert-history', shopId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('alert_history')
        .select('*')
        .eq('shop_id', shopId)
        .order('triggered_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data;
    }
  });

  const getMetricLabel = (type: string) => {
    const labels: Record<string, string> = {
      'technician_efficiency': 'Technician Efficiency',
      'comeback_rate': 'Comeback Rate',
      'waiter_promise': 'Waiter Promise',
      'job_performance': 'Job Performance'
    };
    return labels[type] || type;
  };

  const getAlertIcon = (metricType: string, metricValue: number, thresholdValue: number) => {
    const isAbove = metricValue > thresholdValue;
    
    if (metricType === 'comeback_rate') {
      return isAbove ? <TrendingUp className="h-4 w-4 text-destructive" /> : <TrendingDown className="h-4 w-4 text-success" />;
    }
    
    return isAbove ? <TrendingUp className="h-4 w-4 text-success" /> : <TrendingDown className="h-4 w-4 text-destructive" />;
  };

  if (isLoading) {
    return <Card className="p-4"><div className="h-64 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!alerts || alerts.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Recent Alerts</h3>
        </div>
        <p className="text-sm text-muted-foreground">No alerts triggered yet</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold text-foreground">Recent Alerts</h3>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {alerts.map((alert) => (
          <div key={alert.id} className="p-3 bg-muted/50 rounded-lg border border-border">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {getAlertIcon(alert.metric_type, alert.metric_value, alert.threshold_value)}
                <span className="font-medium text-sm text-foreground">
                  {getMetricLabel(alert.metric_type)}
                </span>
              </div>
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {formatDistanceToNow(new Date(alert.triggered_at), { addSuffix: true })}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-muted-foreground">Current Value</p>
                <p className="font-mono font-medium text-foreground">{alert.metric_value.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-muted-foreground">Threshold</p>
                <p className="font-mono font-medium text-foreground">{alert.threshold_value}%</p>
              </div>
            </div>

            {alert.metadata && Object.keys(alert.metadata).length > 0 && (
              <div className="mt-2 pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  {JSON.stringify(alert.metadata)}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
